import { Injectable } from '@angular/core';

/**
 * LocalStorage provider.
 * Provides persistent browser storage using the Web Storage API.
 */
@Injectable({ providedIn: 'root' })
export class LocalStorageProvider {
  /**
   * Retrieves and deserializes a value from localStorage.
   * @param key - The storage key
   * @returns The parsed value or null if not found or parsing fails
   */
  get<T>(key: string): T | null {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      // Return null on error - storage read failures are handled gracefully
      return null;
    }
  }

  /**
   * Serializes and stores a value in localStorage.
   * @param key - The storage key
   * @param value - The value to store
   */
  set<T>(key: string, value: T): void {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage errors are handled silently
      // QuotaExceededError can occur if storage is full
      // In production, consider implementing a proper logging service
    }
  }

  /**
   * Removes an item from localStorage.
   * @param key - The storage key to remove
   */
  remove(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage errors are handled silently
    }
  }

  /**
   * Clears all items from localStorage.
   * Warning: This will clear ALL localStorage data for the current origin.
   */
  clear(): void {
    try {
      localStorage.clear();
    } catch {
      // Storage errors are handled silently
    }
  }

  /**
   * Checks if a key exists in localStorage.
   * @param key - The storage key to check
   * @returns True if the key exists, false otherwise
   */
  has(key: string): boolean {
    return localStorage.getItem(key) !== null;
  }
}
